// 生態系治理顯示層模型(S37)。狀態 enum 值來自後端 wire,顯示層全中文;
// 巢狀/lineage 推導皆為前端純函式(便於單測)。RBAC:依 IA 定案 §3.2,
// 本域寫入 = admin + platform-owner(security-officer 唯讀;cs-readonly/ai-ops 無存取,
// 由 nav write 旗標隱藏);前端守門只影響 UI,伺服端 requireAdminWrite 仍是真正邊界。
// ⚠️ ecosystem 審核不設 dual-control(IA §3.2 註 2:申請者 org 與審核者 platform admin
// 天然分離,reason/case 必填 + 全鏈 audit 已足);suspend 為可逆治理動作,採二段確認。

import type { Ecosystem, EcosystemRequest } from './ecosystemApi';

// kinio.ai 平台生態系 —— 不可退出/刪除/suspend(common#24 業主定案);UI 不提供破壞性操作。
export const PLATFORM_ECOSYSTEM_ID = 'eco-kinio-ai';

// ---- 域級寫守門(IA specs/01 §3.1/§3.2) ----

export function canEcosystemWrite(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'platform-owner';
}

/** platform 生態系(kinio.ai):UI 不得提供 suspend/retire 等破壞性動作。 */
export function isPlatformEcosystem(eco: Pick<Ecosystem, 'ecosystem_id' | 'kind'>): boolean {
  return eco.kind === 'platform' || eco.ecosystem_id === PLATFORM_ECOSYSTEM_ID;
}

/** 可被 suspend 的條件:非平台、且目前為 active/pending_review(已 suspended/retired/rejected 不再重複)。 */
export function canSuspend(eco: Pick<Ecosystem, 'ecosystem_id' | 'kind' | 'status'>): boolean {
  if (isPlatformEcosystem(eco)) return false;
  return eco.status === 'active' || eco.status === 'pending_review';
}

// ---- kind / status 顯示 ----

export function ecoKindLabel(kind?: string): string {
  if (kind === 'platform') return '平台';
  if (kind === 'org_default') return '組織預設';
  if (kind === 'custom') return '自訂';
  return kind || '—';
}

export function ecoKindTone(kind?: string): string {
  if (kind === 'platform') return 'pill-info';
  if (kind === 'org_default') return 'pill-neutral';
  if (kind === 'custom') return 'pill-good';
  return 'pill-neutral';
}

export function ecoStatusLabel(status?: string): string {
  if (status === 'pending_review') return '待審核';
  if (status === 'active') return '生效中';
  if (status === 'rejected') return '已駁回';
  if (status === 'suspended') return '已停權';
  if (status === 'retired') return '已退役';
  return status || '未知';
}

export function ecoStatusTone(status?: string): string {
  if (status === 'active') return 'pill-good';
  if (status === 'pending_review') return 'pill-warn';
  if (status === 'suspended') return 'pill-warn';
  if (status === 'rejected' || status === 'retired') return 'pill-neutral';
  return 'pill-neutral';
}

// ---- 審核佇列(create request)顯示 ----

export function requestTypeLabel(type?: string): string {
  if (type === 'create') return '創建';
  if (type === 'join') return '加入';
  return type || '—';
}

export function requestStatusLabel(status?: string): string {
  if (status === 'pending') return '待審核';
  if (status === 'approved') return '已核准';
  if (status === 'rejected') return '已駁回';
  return status || '未知';
}

export function requestStatusTone(status?: string): string {
  if (status === 'approved') return 'pill-good';
  if (status === 'pending') return 'pill-warn';
  if (status === 'rejected') return 'pill-neutral';
  return 'pill-neutral';
}

/** 待審核(pending)判定 —— 決定是否顯示核准/駁回操作。 */
export function isPendingRequest(req: Pick<EcosystemRequest, 'status'>): boolean {
  return req.status === 'pending';
}

// ---- admin#6 read model:成員狀態 + via-chain 顯示 ----

/** 成員列(ecosystem_members row)狀態顯示。含 org 成員與巢狀子生態系的邊狀態。 */
export function memberStatusLabel(status?: string): string {
  if (status === 'active') return '生效中';
  if (status === 'invited') return '已邀請';
  if (status === 'requested') return '申請中';
  if (status === 'removed') return '已移除';
  return status || '—';
}

export function memberStatusTone(status?: string): string {
  if (status === 'active') return 'pill-good';
  if (status === 'invited' || status === 'requested') return 'pill-warn';
  if (status === 'removed') return 'pill-neutral';
  return 'pill-neutral';
}

/** via-chain(ecosystem id 串)顯示成 A → B → C;空/單一皆安全回落。 */
export function formatViaChain(chain?: string[]): string {
  return Array.isArray(chain) && chain.length > 0 ? chain.join(' → ') : '—';
}

// ---- inventory 統計 / 分組(巢狀 owner org 視角) ----

export interface InventorySummary {
  total: number;
  active: number;
  pendingReview: number;
  suspended: number;
  custom: number;
}

export function summarizeInventory(items: Ecosystem[]): InventorySummary {
  return {
    total: items.length,
    active: items.filter((e) => e.status === 'active').length,
    pendingReview: items.filter((e) => e.status === 'pending_review').length,
    suspended: items.filter((e) => e.status === 'suspended').length,
    custom: items.filter((e) => e.kind === 'custom').length,
  };
}

export interface OwnerOrgGroup {
  ownerOrgId: string;
  ecosystems: Ecosystem[];
}

/**
 * 依 owner org 分組(inventory 目前能誠實提供的「巢狀」視角:同 org 擁有的生態系聚在一起)。
 * platform(owner 為空)歸入 '(平台)' 群。群內依 kind(platform→org_default→custom)+建立時間排序。
 * ⚠️ 這不是「成員遞移閉包」——ecosystem 之間的巢狀成員關係與 effective 展開尚未曝給 admin
 * read model(見 EcosystemsPage 誠實缺口說明)。
 */
export function groupByOwnerOrg(items: Ecosystem[]): OwnerOrgGroup[] {
  const kindOrder: Record<string, number> = { platform: 0, org_default: 1, custom: 2 };
  const map = new Map<string, Ecosystem[]>();
  for (const e of items) {
    const key = e.owner_org_id?.trim() || '(平台)';
    const bucket = map.get(key) ?? [];
    bucket.push(e);
    map.set(key, bucket);
  }
  const groups: OwnerOrgGroup[] = [];
  for (const [ownerOrgId, ecosystems] of map) {
    ecosystems.sort((a, b) => {
      const ka = kindOrder[a.kind] ?? 9;
      const kb = kindOrder[b.kind] ?? 9;
      if (ka !== kb) return ka - kb;
      return (a.created_at || 0) - (b.created_at || 0);
    });
    groups.push({ ownerOrgId, ecosystems });
  }
  // 平台群置頂,其餘依 owner org id 排序,穩定可測。
  groups.sort((a, b) => {
    if (a.ownerOrgId === '(平台)') return -1;
    if (b.ownerOrgId === '(平台)') return 1;
    return a.ownerOrgId.localeCompare(b.ownerOrgId);
  });
  return groups;
}

/**
 * 交叉比對創建審核紀錄:找出「生出這個 ecosystem 的那筆 create request」,供 detail lineage 顯示
 * (誰申請、何時、審核人/理由/案件)。這是 admin 能誠實提供的 lineage —— 生態系「怎麼來的」;
 * 「成員鏈怎麼帶進哪個 org」的 effective 展開仍待後端曝端點。
 */
export function findCreateRequest(requests: EcosystemRequest[], ecosystemId: string): EcosystemRequest | undefined {
  return requests.find((r) => r.type === 'create' && r.ecosystem_id === ecosystemId);
}

export const KIND_OPTIONS = [
  { value: '', label: '全部類型' },
  { value: 'platform', label: '平台' },
  { value: 'org_default', label: '組織預設' },
  { value: 'custom', label: '自訂' },
] as const;

export const STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'pending_review', label: '待審核' },
  { value: 'active', label: '生效中' },
  { value: 'suspended', label: '已停權' },
  { value: 'rejected', label: '已駁回' },
  { value: 'retired', label: '已退役' },
] as const;

export const REQUEST_STATUS_OPTIONS = [
  { value: 'pending', label: '待審核' },
  { value: 'approved', label: '已核准' },
  { value: 'rejected', label: '已駁回' },
  { value: 'all', label: '全部歷史' },
] as const;

// 審核佇列的 type 分頁:創建(admin 有核准權)vs 加入(admin 僅監督,核准權在 owner org)。
export const REQUEST_TYPE_OPTIONS = [
  { value: 'create', label: '創建申請' },
  { value: 'join', label: '加入申請(監督)' },
] as const;
